import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import type { CertificateSearchParams } from '../data-access/index.js';
import { useDebounced } from './use-debounced.js';

export const ALL_STATUSES = 'all';

export function useCertificateListState({ pageSize }: { pageSize: number }) {
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);
  const [status, setStatus] = useState(ALL_STATUSES);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput, 300);

  const backToFirstPage = () =>
    setPagination((previous) =>
      previous.pageIndex === 0 ? previous : { ...previous, pageIndex: 0 },
    );

  const changeSorting: OnChangeFn<SortingState> = (updater) => {
    setSorting(updater);
    backToFirstPage();
  };

  const changeStatus = (next: string) => {
    setStatus(next);
    backToFirstPage();
  };

  const changeSearch = (next: string) => {
    setSearchInput(next);
    backToFirstPage();
  };

  const reset = () => {
    changeStatus(ALL_STATUSES);
    changeSearch('');
  };

  const isFiltered = status !== ALL_STATUSES || searchInput.trim().length > 0;

  const params = useMemo<CertificateSearchParams>(() => {
    const sort = sorting[0];
    return {
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sort: sort ? { field: sort.id, desc: sort.desc } : undefined,
      status: status === ALL_STATUSES ? undefined : status,
      query: search,
      excludeDeleted: true,
    };
  }, [pagination, sorting, status, search]);

  return {
    params,
    pagination,
    setPagination,
    sorting,
    setSorting: changeSorting,
    status,
    searchInput,
    isFiltered,
    changeStatus,
    changeSearch,
    reset,
  };
}
